import { useCallback, useEffect, useState } from 'react';
import { Platform, View } from 'react-native';

import { Badge, Button, Text } from '@/components/ui';
import { useSidebar } from '@/components/layout/SidebarContext';
import { getSyncEngine } from '@/lib/sync/engine';
import { useSyncConnection } from '@/lib/sync/use-connection';

type LocalStats = {
  blocks: number;
  files: number;
  storageBytes: number | null;
};

function formatBytes(bytes: number | null): string {
  if (bytes == null) {
    return 'unknown';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatRelative(epochMs: number | null): string {
  if (!epochMs) {
    return 'never';
  }
  const seconds = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (seconds < 5) {
    return 'just now';
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}

async function loadLocalStats(): Promise<LocalStats> {
  const db = getSyncEngine().getDatabase();
  let blocks = 0;
  let files = 0;
  if (db) {
    const blockRow = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM blocks WHERE deleted_at IS NULL`,
    );
    const fileRow = await db.getFirstAsync<{ n: number }>(`SELECT COUNT(*) AS n FROM file_objects`);
    blocks = blockRow?.n ?? 0;
    files = fileRow?.n ?? 0;
  }
  let storageBytes: number | null = null;
  if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      storageBytes = estimate.usage ?? null;
    } catch {
      storageBytes = null;
    }
  }
  return { blocks, files, storageBytes };
}

function StatusBadge({ status }: { status: 'online' | 'offline' | 'connecting' }) {
  if (status === 'online') {
    return <Badge label="Online" variant="success" />;
  }
  if (status === 'offline') {
    return <Badge label="Offline" variant="warning" />;
  }
  return <Badge label="Connecting…" variant="neutral" />;
}

/**
 * Visibility and controls for the local (offline) cache: connection health, how
 * much data is stored on this device, any deltas that failed to sync, and manual
 * recovery — retry failed items or a full resync from the server.
 */
export function SyncStatusCard() {
  const connection = useSyncConnection();
  const { refreshTree } = useSidebar();
  const [stats, setStats] = useState<LocalStats | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'refresh' | 'retry' | 'resync'>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refreshStats = useCallback(async () => {
    try {
      const [next, quarantine] = await Promise.all([
        loadLocalStats(),
        getSyncEngine().getQuarantineEntries(),
      ]);
      setStats(next);
      setLastError(quarantine[0]?.error ?? null);
    } catch {
      // Best-effort; leave prior values in place.
    }
  }, []);

  useEffect(() => {
    void refreshStats();
  }, [refreshStats, connection.quarantinedCount, connection.pendingCount]);

  const onRefresh = useCallback(async () => {
    setBusy('refresh');
    setMessage(null);
    try {
      await getSyncEngine().catchup(true);
      refreshTree();
      await refreshStats();
      setMessage('Synced.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setBusy(null);
    }
  }, [refreshStats, refreshTree]);

  const onRetry = useCallback(async () => {
    setBusy('retry');
    setMessage(null);
    try {
      const remaining = await getSyncEngine().retryFailedItems();
      refreshTree();
      await refreshStats();
      setMessage(remaining === 0 ? 'All items synced.' : `${remaining} still failing.`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setBusy(null);
    }
  }, [refreshStats, refreshTree]);

  const runResync = useCallback(async () => {
    setBusy('resync');
    setMessage(null);
    try {
      await getSyncEngine().fullResync();
      refreshTree();
      await refreshStats();
      setMessage('Full resync complete.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Resync failed');
    } finally {
      setBusy(null);
    }
  }, [refreshStats, refreshTree]);

  const onFullResync = useCallback(() => {
    const confirmMessage =
      'Re-download all data from the server, replacing this device’s local copy? Pending local changes are pushed first. This can take a moment on large workspaces.';
    if (Platform.OS === 'web') {
      if (window.confirm(confirmMessage)) {
        void runResync();
      }
      return;
    }
    void runResync();
  }, [runResync]);

  const hasFailures = connection.quarantinedCount > 0;

  return (
    <View className="mt-6 max-w-md rounded-lg border border-border bg-surface-raised p-3">
      <View className="flex-row items-center gap-2">
        <Text variant="body" className="font-medium">
          Sync &amp; storage
        </Text>
        <StatusBadge status={connection.status} />
      </View>

      <View className="mt-3 gap-1">
        <View className="flex-row justify-between">
          <Text variant="caption" className="text-muted">
            Last synced
          </Text>
          <Text variant="caption">{formatRelative(connection.lastSyncedAt)}</Text>
        </View>
        <View className="flex-row justify-between">
          <Text variant="caption" className="text-muted">
            Pending local changes
          </Text>
          <Text variant="caption">{connection.pendingCount}</Text>
        </View>
        <View className="flex-row justify-between">
          <Text variant="caption" className="text-muted">
            Stored on this device
          </Text>
          <Text variant="caption">
            {stats
              ? `${stats.blocks} blocks · ${stats.files} files${
                  stats.storageBytes != null ? ` · ${formatBytes(stats.storageBytes)}` : ''
                }`
              : '…'}
          </Text>
        </View>
        <View className="flex-row justify-between">
          <Text variant="caption" className="text-muted">
            Failed to sync
          </Text>
          <Text variant="caption" className={hasFailures ? 'text-danger' : undefined}>
            {connection.quarantinedCount}
          </Text>
        </View>
      </View>

      {hasFailures && lastError ? (
        <Text variant="caption" className="mt-2 text-danger">
          Last error: {lastError}
        </Text>
      ) : null}

      <View className="mt-3 flex-row flex-wrap gap-2">
        <Button
          variant="secondary"
          label={busy === 'refresh' ? 'Syncing…' : 'Sync now'}
          disabled={busy != null}
          onPress={() => void onRefresh()}
        />
        {hasFailures ? (
          <Button
            variant="secondary"
            label={busy === 'retry' ? 'Retrying…' : 'Retry failed items'}
            disabled={busy != null}
            onPress={() => void onRetry()}
          />
        ) : null}
        <Button
          variant="danger"
          label={busy === 'resync' ? 'Resyncing…' : 'Full resync'}
          disabled={busy != null}
          onPress={onFullResync}
        />
      </View>

      {message ? (
        <Text variant="caption" className="mt-2 text-muted">
          {message}
        </Text>
      ) : null}

      <Text variant="caption" className="mt-2 text-muted">
        A full resync pulls the server’s current state to fix any divergence. Your unsynced edits are
        pushed first, so nothing local is lost.
      </Text>
    </View>
  );
}
